import React, { useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { FileText, FileSpreadsheet, FileType, Download, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const ExportCard = ({ icon: Icon, title, desc, format, color, testId }) => {
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/export/${format}`, { responseType: "blob" });
      const blob = new Blob([resp.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "csv" ? "rciq_denials.csv" : format === "excel" ? "rciq_executive_report.xlsx" : "rciq_executive_report.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch {
      toast.error("Export failed. Ensure a dataset is loaded.");
    } finally { setLoading(false); }
  };
  return (
    <div data-testid={testId} className="surface-card p-6 group hover:-translate-y-0.5 transition-all duration-200">
      <div className={`h-12 w-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{desc}</p>
      <Button onClick={download} disabled={loading} className="mt-5 w-full bg-navy hover:bg-[#0B3A66] text-white rounded-lg" data-testid={`download-${format}-button`}>
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        Download
      </Button>
    </div>
  );
};

export default function ExportsPage() {
  return (
    <AppShell>
      <div className="max-w-5xl mx-auto space-y-6 fade-up">
        <div>
          <span className="label-overline">Reports</span>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-2 tracking-tight">Export Reports</h1>
          <p className="text-slate-500 mt-2">Export executive-ready reports from the Denial Intelligence & Recovery Workflow Platform — de-identified, payor-aware, and operationally actionable.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          <ExportCard testId="export-card-pdf" icon={FileText} title="Executive Report PDF" desc="Multi-page board-ready PDF with KPIs, top denials, payor performance, aged AR, monthly trends, and an intelligence brief." format="pdf" color="bg-navy" />
          <ExportCard testId="export-card-excel" icon={FileSpreadsheet} title="Excel Workbook" desc="Multi-sheet workbook: KPIs, top denial categories, payor mix, and monthly trends." format="excel" color="bg-teal" />
          <ExportCard testId="export-card-csv" icon={FileType} title="De-Identified Dataset" desc="De-identified operational denial data suitable for advanced analysis and reporting." format="csv" color="bg-cyan-brand" />
        </div>
      </div>
    </AppShell>
  );
}
